import type { OrderedMap, RecordOf } from 'immutable';
import { Map as ImmutableMap } from 'immutable';
import memoizeOne from 'memoize-one';
import type { SyntheticEvent } from 'react';
import React, { memo, useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { DateTime } from 'luxon';

import { authActions, authSelectors, authTypes } from 'app-components/flux/auth';
import { datasetsSelectors, datasetsUtils } from 'app-components/flux/datasets';
import { entitlementsSelectors } from 'app-components/flux/entitlements';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import type { Milestone } from 'app-components/flux/milestones';
import { preferencesSelectors } from 'app-components/flux/preferences';
import { preferencesV2Actions, preferencesV2Selectors, preferencesV2Types } from 'app-components/flux/preferences-v2';
import { profileSelectors } from 'app-components/flux/profile';
import { rootUiActions, rootUiTypes } from 'app-components/flux/root-ui';
import { subscriptionsSelectors } from 'app-components/flux/subscriptions';
import { transactionsSelectors } from 'app-components/flux/transactions';
import { config, platform, requireEnvironmentConfig, tracker } from 'app-components/utils/core';
import store from 'app-components/utils/redux-store';
import { alpha } from '@mui/material/styles';
import {
  DIALOG_TYPE as DIALOG_NOTIFICATION,
  mkNotificationDialogProps,
} from 'components/Dialogs/NotificationDialog/types';
import { DIALOG_TYPE_REPORT_ERROR } from 'components/Dialogs/ReportErrorDialog';
import { dispatchSimpleNotification } from 'data/notifications/notificationsUtils';
import { saveStore } from 'utils/storeUtils';

import { trackTopBarNavMenuClick } from './trackTopBarNavMenuClick';

import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import CheckIcon from '@mui/icons-material/Check';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DarkModeRounded from '@mui/icons-material/DarkModeRounded';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import LightModeRounded from '@mui/icons-material/LightModeRounded';
import MonitorRoundedIcon from '@mui/icons-material/MonitorRounded';
import AttachMoneyRoundedIcon from '@mui/icons-material/AttachMoneyRounded';
import EmergencyRoundedIcon from '@mui/icons-material/EmergencyRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import type { PopoverActions } from '@mui/material/Popover';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import lifeHubLogoDark from 'assets/logos/quicken-lifehub-logo-dark.svg';
import lifeHubLogo from 'assets/logos/quicken-lifehub-logo.svg';
import simplifiLogoLight from 'assets/logos/simplifi-logo-light.svg';
import MiniDatasetPicker from 'components/MiniDatasetPicker';
import useQPreferences from 'components/QPreferences/useQPreferences';
import QSegmentedTabs from 'components/QSegmentedTabs';
import { makeStyles } from 'tss-react/mui';
import { achievementsActions } from 'app-components/flux/achievements';
import type { Dataset } from 'app-components/flux/datasets/datasetsTypes';
import { milestonesActions, milestonesSelectors } from 'app-components/flux/milestones';
import type { Profile } from 'app-components/flux/profile/profileTypes';
import Dump from 'components/Dump';
import QButton from 'components/QButton';
import { useAppSelector } from 'hooks/redux';
import { useNavigate } from 'react-router-dom';
import * as sessionStorageEx from 'utils/sessionStorageEx';

const getOrderedDatasets = memoizeOne((currentDataset, datasets) => {
  const data = [];
  if (currentDataset) data.push(currentDataset);
  datasets?.forEach((dataset: Dataset) => {
    if (dataset?.id && dataset?.id !== currentDataset?.id && data.length < 3) {
      data.push(dataset);
    }
  });
  return data;
});

const getFullName = (profile: Profile | undefined) => {
  if (!profile) {
    return '';
  }
  let fullName = '';
  if (profile.firstName && profile.lastName) {
    fullName = `${profile.firstName} ${profile.lastName}`;
  } else if (profile.primaryAddress && profile.primaryAddress.fullName) {
    fullName = profile.primaryAddress.fullName;
  }
  return fullName;
};

const popOverWidth = 320;

const useStyles = makeStyles()((theme) => ({
  datasetSwitchingProgress: {
    marginTop: '30px',
  },
  datasetSwitchingContent: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  profilePopover: {
    minWidth: popOverWidth,
    padding: `${theme.spacing(2)} ${theme.spacing(1)} ${theme.spacing(2)} ${theme.spacing(1)}`,
  },
  profileHeader: {
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    overflow: 'visible',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  profileDataset: {
    backgroundColor: alpha(theme.palette.background.default, 0.8),
    borderRadius: 8,
    padding: '5px 8px 5px 8px',
  },
  rowWithIconSpan: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    position: 'relative',
    alignItems: 'center',
  },
  leftSpan: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  leftText: {
    marginLeft: theme.spacing(1),
  },
  milestoneBadge: {
    height: 24,
    width: 24,
    marginRight: -8,
    position: 'relative',
  },
  datasetListItemDefault: {
    paddingTop: 4,
    paddingBottom: 4,
  },
  dismissIcon: {
    color: theme.palette.icon.light,
    height: 20,
    width: 20,
    padding: theme.spacing(2),
    marginRight: `-${theme.spacing(1)}`,
    marginTop: `-${theme.spacing(2)}`,
  },
  lifeHubContainer: {
    borderRadius: 8,
    padding: `${theme.spacing(2)} ${theme.spacing(1)} ${theme.spacing(2)} ${theme.spacing(1)}`,
    backgroundColor: theme.palette.info.light,
    display: 'flex',
    flexDirection: 'row',
  },
  logo: {
    width: 24,
    height: 24,
  },
  lifeHubLogo: {
    width: 88,
    marginBottom: theme.spacing(1),
  },
  lifeHubButton: {
    marginLeft: -16,
    marginBottom: -12,
  },
  lifeHubLeft: {
    marginRight: theme.spacing(2),
    display: 'flex',
  },
  lifeHubMiddle: {
    alignItems: 'flex-start',
    flexDirection: 'column',
    display: 'flex',
  },
  lifeHubDescription: {},
  lifeHubRight: {
    width: 20,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  textPrimary: {
    color: theme.palette.text.primary,
  },
  textSecondary: {
    color: theme.palette.text.secondary,
  },
  linkIcon: {
    width: 16,
    height: 16,
    color: theme.palette.icon.disabled,
  },
  divider: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  listItems: {
    padding: 0,
  },
  checkStyle: {
    color: theme.palette.primary.main,
  },
}));

function IconRowSpan({
  text,
  iconComponent,
  leftIcon,
}: {
  text: string;
  iconComponent?: React.ReactNode;
  leftIcon?: React.ReactNode;
}) {
  const { classes } = useStyles();
  return (
    <span className={classes.rowWithIconSpan}>
      <span className={classes.leftSpan}>
        {leftIcon}
        <Typography className={leftIcon ? classes.leftText : ''} variant='paragraphRegular'>
          {text}
        </Typography>
      </span>
      {iconComponent}
    </span>
  );
}

function AppearanceToggle() {
  const { setDatasetPreference } = useQPreferences();
  const currentTheme = useAppSelector(
    (state) => preferencesSelectors.getSharedPreferencesByPath(state, { group: 'dataset', path: ['webApp'] })?.theme,
  );

  const handleThemeChange = (_event: SyntheticEvent, value: string) => {
    if (value) {
      tracker.track(tracker.events.appearance, { themeKey: value });
      setDatasetPreference({ theme: value });
    }
  };

  return (
    <Stack direction='row' alignItems='center' gap={1}>
      <Typography variant='captionRegular' color='text.tertiary' sx={{ marginTop: '4px' }}>
        {currentTheme === 'ACME_DARK' ? 'Dark' : currentTheme === 'system-appearance' ? 'System' : 'Light'}
      </Typography>
      <QSegmentedTabs
        size='medium'
        value={
          currentTheme === 'ACME_DARK'
            ? 'ACME_DARK'
            : currentTheme === 'system-appearance'
              ? 'system-appearance'
              : 'muiDefault'
        }
        onChange={handleThemeChange}
      >
        <ToggleButton value='muiDefault' aria-label='Light mode'>
          <LightModeRounded fontSize='inherit' />
        </ToggleButton>
        <ToggleButton value='ACME_DARK' aria-label='Dark mode'>
          <DarkModeRounded fontSize='inherit' />
        </ToggleButton>
        <ToggleButton value='system-appearance' aria-label='System appearance mode'>
          <MonitorRoundedIcon fontSize='inherit' />
        </ToggleButton>
      </QSegmentedTabs>
    </Stack>
  );
}

function PrivacyModeToggle() {
  const dispatch = useDispatch();
  const privacyModeFeatureFlag = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'privacyMode'));
  const privacyModeEnabled = useAppSelector((state) =>
    preferencesV2Selectors.getPreferenceValue(state, 'sPrivacyMode', false),
  );

  const handleChange = (_event: SyntheticEvent, value: string) => {
    if (value != null) {
      trackTopBarNavMenuClick({
        menuItem: 'Privacy Mode',
        section: 'topbar-profile',
        value: value === 'on' ? 'on' : 'off',
      });
      dispatch(
        preferencesV2Actions.updatePreferenceOverride(
          preferencesV2Types.mkPreferenceV2({
            id: 'sPrivacyMode',
            value: value === 'on',
          }),
        ),
      );
    }
  };

  if (!privacyModeFeatureFlag) {
    return null;
  }

  return (
    <MenuItem id='privacy-mode-menu-item' onClick={(e) => e.stopPropagation()}>
      <IconRowSpan
        text='Privacy mode'
        iconComponent={
          <Stack direction='row' alignItems='center' gap={1}>
            <Typography variant='captionRegular' color='text.tertiary' sx={{ marginTop: '4px' }}>
              {privacyModeEnabled ? 'Amounts hidden' : 'Off'}
            </Typography>
            <QSegmentedTabs size='medium' value={privacyModeEnabled ? 'on' : 'off'} onChange={handleChange}>
              <ToggleButton value='off' aria-label='Amounts visible'>
                <AttachMoneyRoundedIcon fontSize='inherit' />
              </ToggleButton>
              <ToggleButton value='on' aria-label='Amounts hidden'>
                <EmergencyRoundedIcon fontSize='inherit' />
              </ToggleButton>
            </QSegmentedTabs>
          </Stack>
        }
      />
    </MenuItem>
  );
}

function AchievementsMenuItem({ closeProfile }: { closeProfile: () => void }) {
  const navigate = useNavigate();
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const recentlyEarned: Milestone[] = useAppSelector(
    (state) => milestonesSelectors.getUpNextMilestones(state).recentlyEarned,
  );

  const iconComponent = (
    <div>
      {recentlyEarned?.slice(0, 4)?.map((badge, index) => (
        <img
          src={badge.get('imageLink') ?? undefined}
          key={`${badge.get('id')}-profile-menu`}
          alt={'badge'}
          className={classes.milestoneBadge}
          style={{ zIndex: 100 - index }}
        />
      ))}
    </div>
  );

  return (
    <MenuItem
      id={'achievements-profile-menu'}
      onClick={() => {
        dispatch(achievementsActions.getAchievements());
        dispatch(milestonesActions.getMilestones());
        closeProfile();
        navigate('/achievements');
        trackTopBarNavMenuClick({ menuItem: 'Achievements', section: 'topbar-profile' });
        tracker.track(tracker.events.achievementsPageView, { initiator: 'nav bar' });
      }}
    >
      <IconRowSpan text='Achievements' iconComponent={iconComponent} />
    </MenuItem>
  );
}

type ProfileAvatarProps = {
  label: string;
  onClick?: (e: SyntheticEvent) => void;
  id?: string;
};

/** Box + Avatar with gradient ring. Use as trigger (pass onClick) or display inside the menu (no onClick). */
function ProfileAvatar({ label, onClick, id }: ProfileAvatarProps) {
  const theme = useTheme();
  const isButton = onClick != null;
  return (
    <Box
      sx={{
        padding: '2px',
        background: `linear-gradient(45deg, ${theme.palette.secondary.light}, ${theme.palette.primary.light})`,
        borderRadius: '50%',
      }}
    >
      <Avatar
        component={isButton ? 'button' : 'div'}
        id={id ?? (isButton ? 'topbar-profile-menu' : undefined)}
        sx={{
          background: `linear-gradient(45deg, ${theme.palette.secondary.light}, ${theme.palette.info.light})`,
          color: theme.palette.text.primary,
          // Reset the UA button padding (1px 6px): it leaves 24px, too narrow for wide Inter initials such as "MM"
          ...(isButton && { border: 'none', padding: 0, cursor: 'pointer' }),
          whiteSpace: 'nowrap',
          width: 36,
          height: 36,
          boxShadow: theme.shadows[4],
        }}
        onClick={onClick}
      >
        <Typography variant='paragraphMedium'>{label}</Typography>
      </Avatar>
    </Box>
  );
}

type ProfileSectionProps = {
  showHiddenMenu: boolean;
  dataset: Dataset | undefined;
  avatarLabel?: string;
};

function ProfileSection({ showHiddenMenu, dataset }: ProfileSectionProps) {
  const profile: Profile | undefined = useAppSelector(profileSelectors.getProfile);
  const subscription = useAppSelector(subscriptionsSelectors.getActiveSubscription);
  const fullName = getFullName(profile);
  const theme = useTheme();
  const { classes } = useStyles();

  if (!profile) {
    return null;
  }
  return (
    <div className={classes.profileHeader}>
      <Dump obj={{ dataset, profile, subscription }} />

      <Stack alignItems='center' gap={0.5} sx={{ textAlign: 'center' }}>
        <Typography variant='titleMedium' className={classes.textPrimary}>
          {fullName}
        </Typography>
        <Typography variant='paragraphRegular' className={classes.textSecondary}>
          {profile.username}
        </Typography>
      </Stack>

      {showHiddenMenu && dataset && (
        <Stack
          id='my-account-dataset-id'
          sx={{ border: `1px dashed ${theme.palette.divider}`, padding: 1, borderRadius: '8px' }}
        >
          <Typography variant='paragraphMedium' fontFamily='monospace'>
            user summary:
            <IconButton
              size='small'
              onClick={() => {
                const state = store?.getState();
                if (state) {
                  const transactions = transactionsSelectors.getResourcesById(state);
                  navigator.clipboard.writeText(
                    `email: ${profile?.username}\n` +
                      `user id: ${profile?.userId}\n` +
                      `dataset name: ${dataset?.name}\n` +
                      `dataset id: ${dataset?.id}\n` +
                      `transactions count: ${transactions?.size}\n` +
                      `environment: ${config?.client?.hostname}/${config?.client?.environment}\n` +
                      `version: ${config?.client?.app_version}\n` +
                      `build: ${config?.client?.build}/${config?.client?.commit} (${config?.client?.build_type})\n` +
                      `browser: ${platform?.browser?.name} ${platform?.browser?.version}\n` +
                      `OS: ${platform?.os?.name} ${platform?.os?.version}`,
                  );
                  dispatchSimpleNotification('User Summary is copied to clipboard');
                }
              }}
            >
              <FileCopyOutlinedIcon fontSize='inherit' />
            </IconButton>
          </Typography>

          <Typography variant='paragraphMedium' fontFamily='monospace'>
            user id: {profile.userId}
            <IconButton
              size='small'
              onClick={() => {
                navigator.clipboard.writeText(profile.userId || 'Unknown');
                dispatchSimpleNotification('User Id is copied to clipboard');
              }}
            >
              <FileCopyOutlinedIcon fontSize='inherit' />
            </IconButton>
          </Typography>

          <Typography variant='paragraphMedium' fontFamily='monospace'>
            dataset id: {dataset.id}
            <IconButton
              size='small'
              onClick={() => {
                navigator.clipboard.writeText(dataset.id || 'Unknown');
                dispatchSimpleNotification('Dataset Id is copied to clipboard');
              }}
            >
              <FileCopyOutlinedIcon fontSize='inherit' />
            </IconButton>
          </Typography>
        </Stack>
      )}
    </div>
  );
}

type TopBarProfileMenuProps = {
  hideFeatures: boolean;
  id?: string;
};

function TopBarProfileMenu({ hideFeatures, id }: TopBarProfileMenuProps) {
  const [profileButtonAnchorEl, setProfileButtonAnchorEl] = useState<null | Element>(null);
  const [showHiddenMenu, setShowHiddenMenu] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const popoverActions = React.createRef<PopoverActions>();
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const theme = useTheme();
  const createDialog = (uiRootData: RecordOf<rootUiTypes.RootUiData>) =>
    dispatch(rootUiActions.createDialog(uiRootData));
  const profile: Profile | undefined = useAppSelector(profileSelectors.getProfile);
  const fullName = getFullName(profile);

  let initials = '';
  if (profile?.firstName) {
    initials += profile.firstName.trim().charAt(0).toUpperCase();
  }
  if (profile?.lastName) {
    initials += profile.lastName.trim().charAt(0).toUpperCase();
  }
  if (!initials && fullName) {
    const parts = fullName.trim().split(/\s+/);
    if (parts[0]) {
      initials += parts[0].charAt(0).toUpperCase();
    }
    if (parts[1]) {
      initials += parts[1].charAt(0).toUpperCase();
    }
  }
  if (!initials && profile?.username) {
    initials = profile.username.trim().charAt(0).toUpperCase();
  }
  const avatarLabel = initials;

  const feedbackEnabled = useAppSelector((state) => featureFlagsSelectors.getFeatureFlags(state).get('feedback'));
  const financesEnabled = useAppSelector((state) => featureFlagsSelectors.getFeatureFlags(state).get('finances'));

  const hasLifeHub = useAppSelector((state) => entitlementsSelectors.getActiveEntitlement(state, 'QEM_BASIC'));
  const lifeHubPref = useAppSelector((state) => preferencesV2Selectors.getPreferenceValue(state, 'sShowLifeHubBanner'));

  const authSessionAccessToken = useAppSelector((state) => authSelectors.getAuthSession(state).accessToken);
  const datasets: OrderedMap<string, Dataset> = useAppSelector(datasetsSelectors.getSortedDatasets);
  const datasetId = useAppSelector(authSelectors.getDatasetId);
  const dataset = datasets.get(datasetId || '');
  const data = getOrderedDatasets(dataset, datasets);

  const datasetsRefs = useRef(datasets);

  useEffect(() => {
    if (datasetsRefs.current !== datasets && profileButtonAnchorEl) {
      popoverActions.current?.updatePosition();
    }
  }, [datasets, popoverActions, profileButtonAnchorEl]);

  const closeProfile = useCallback(() => {
    setProfileButtonAnchorEl(null);
    setShowHiddenMenu(false);
  }, []);

  const myAccountClick = () => {
    trackTopBarNavMenuClick({ menuItem: 'Manage Quicken ID', section: 'topbar-profile' });
    tracker.track(tracker.events.myAccount, { location: 'nav menu' });
    closeProfile();
    window.open(`${requireEnvironmentConfig().myaccount_url}?hide-nav&bearer=${authSessionAccessToken}`);
  };

  const logoutClick = () => {
    trackTopBarNavMenuClick({ menuItem: 'Sign Out', section: 'topbar-profile' });
    sessionStorageEx.removeSandbox();
    dispatch(authActions.authLogout({ reason: 'AUTH_LOGOUT_USER_INITIATED' }, { context: 'profile' }));
  };

  const handleDatasetClick = (dataset: Dataset) => {
    if (datasetId !== dataset.id) {
      trackTopBarNavMenuClick({ menuItem: 'Switch Dataset', section: 'topbar-profile' });
      tracker.track(tracker.events.datasetChange, { location: 'nav menu' });
      setTimeout(
        () =>
          dispatch(authActions.authSelectDataset(authTypes.AuthSelectDatasetPayload({ dataset, userChoice: true }))),
        100,
      );
      closeProfile();
      createDialog(
        rootUiTypes.mkRootUiData({
          type: DIALOG_NOTIFICATION,
          allowNesting: false,
          props: ImmutableMap(
            mkNotificationDialogProps({
              buttonLabel: null,
              content: (
                <div className={classes.datasetSwitchingContent}>
                  <Typography variant='bodyRegular'>Switching to {dataset.name}</Typography>
                  <CircularProgress classes={{ root: classes.datasetSwitchingProgress }} size={50} />
                </div>
              ),
              dialogProps: {
                disableBackdropClick: true,
                disableEscapeKeyDown: true,
              },
              title: '',
            }),
          ),
        }),
      );
    } else {
      setTimeout(closeProfile, 100);
    }
  };

  const handleProfileClose = () => {
    closeProfile();
  };

  const handleOpenLifeHub = () => {
    if (hasLifeHub) {
      trackTopBarNavMenuClick({ menuItem: 'Launch Quicken LifeHub', section: 'topbar-profile' });
      tracker.track(tracker.events.qlhLaunchClicked);
      window.open('https://lifehub.quicken.com/', '_blank');
    } else {
      trackTopBarNavMenuClick({ menuItem: 'Get Started', section: 'topbar-profile' });
      tracker.track(tracker.events.qlhGetStartedClicked);
      window.open('https://www.quicken.com/products/lifehub/', '_blank');
    }
  };

  const onDismissLifeHub = () => {
    tracker.track(tracker.events.qlhBannerDismissed);
    dispatch(
      preferencesV2Actions.updatePreferenceOverride(
        preferencesV2Types.mkPreferenceV2({
          id: 'sShowLifeHubBanner',
          value: false,
        }),
      ),
    );
  };

  const lifeHubPopup = (
    <>
      <Divider className={classes.divider} />
      <div
        id='lifehub-dismissable-item'
        className={classes.lifeHubContainer}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className={classes.lifeHubLeft}>
          <img src={simplifiLogoLight} className={classes.logo} alt='Quicken Simplifi, click to go home' />
        </div>
        <div className={classes.lifeHubMiddle}>
          <img
            src={theme.palette.mode === 'dark' ? lifeHubLogoDark : lifeHubLogo}
            className={classes.lifeHubLogo}
            alt='Quicken LifeHub'
          />
          <Typography className={classes.lifeHubDescription} variant='captionRegular'>
            {"The smarter way to organize, protect, & share life's essential information"}
          </Typography>
          <QButton className={classes.lifeHubButton} variant='text' onClick={handleOpenLifeHub}>
            Get Started
          </QButton>
        </div>
        <div className={classes.lifeHubRight}>
          {isHovered && (
            <IconButton aria-label='Close' id='dlg-close' onClick={onDismissLifeHub} className={classes.dismissIcon}>
              <CloseRoundedIcon />
            </IconButton>
          )}
        </div>
      </div>
    </>
  );

  const showLifeHubBanner = lifeHubPref && !hasLifeHub;
  const showFinances = datasets && datasets.size > 1 && (datasets || financesEnabled);

  const financesSection = (
    <div id='dataset-change-option' className={classes.profileDataset}>
      <Typography color='textPrimary' variant='paragraphMedium'>
        Finances
      </Typography>
      <List disablePadding>
        {data &&
          data.map((theDataset, index) => (
            <ListItemButton
              key={theDataset.id}
              data-testid={theDataset.id}
              onClick={() => handleDatasetClick(theDataset)}
              classes={{ root: classes.datasetListItemDefault }}
              disabled={!datasetsUtils.hasActiveEntitlement(theDataset)}
            >
              <Dump obj={theDataset} />
              <ListItemIcon
                style={{
                  visibility: `${dataset === theDataset ? 'visible' : 'hidden'}`,
                  marginRight: 0,
                }}
              >
                <CheckIcon className={classes.checkStyle} />
              </ListItemIcon>
              <ListItemText
                primary={theDataset.name}
                slotProps={{
                  primary: {
                    variant: 'paragraphRegular',
                    color: dataset === theDataset ? theme.palette.primary.main : theme.palette.text.primary,
                  },
                }}
                id={`data-set-${index + 1}`}
              />
            </ListItemButton>
          ))}

        {datasets && datasets.size > 3 && (
          <MiniDatasetPicker
            datasets={datasets}
            dataset={dataset}
            onChange={(dataset: Dataset) => handleDatasetClick(dataset)}
            label='See all finances'
            id='mini-dataset-picker'
          />
        )}
      </List>
    </div>
  );

  return (
    <>
      <Tooltip title='Profile'>
        <ProfileAvatar
          id={id}
          label={avatarLabel}
          onClick={(e: SyntheticEvent) => setProfileButtonAnchorEl(e.currentTarget)}
        />
      </Tooltip>
      <Popover
        action={popoverActions}
        id='profile-menu'
        open={!!profileButtonAnchorEl}
        anchorEl={profileButtonAnchorEl}
        onClose={handleProfileClose}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'bottom',
        }}
        transformOrigin={{
          horizontal: 'right',
          vertical: 'top',
        }}
        slotProps={{
          paper: {
            elevation: 24,
            style: {
              width: 360,
              transform: 'translate3d(0, 0, 0)',
            },
          },
        }}
        onKeyDown={(e) => {
          if (e.altKey) {
            setShowHiddenMenu(true);
          }
          if (e.key === 'Escape') {
            handleProfileClose();
          }
        }}
        onKeyUp={() => {
          setShowHiddenMenu(false);
        }}
        disableEnforceFocus
      >
        <div className={classes.profilePopover}>
          <ProfileSection showHiddenMenu={showHiddenMenu} dataset={dataset} avatarLabel={avatarLabel} />
          {showFinances && financesSection}
          {showLifeHubBanner && lifeHubPopup}
          <Divider className={classes.divider} />
          <MenuList className={classes.listItems}>
            {hasLifeHub && (
              <MenuItem onClick={handleOpenLifeHub} id='open-lifehub-button'>
                <IconRowSpan
                  text='Launch Quicken LifeHub'
                  iconComponent={<OpenInNewRoundedIcon className={classes.linkIcon} />}
                  leftIcon={
                    <img
                      src={simplifiLogoLight}
                      className={classes.logo}
                      alt='Quicken LifeHub, click to open in new tab'
                    />
                  }
                />
              </MenuItem>
            )}
            {hasLifeHub && <Divider />}
            <MenuItem onClick={myAccountClick} id='my-account-button'>
              <IconRowSpan
                text='Manage Quicken ID'
                iconComponent={<OpenInNewRoundedIcon className={classes.linkIcon} />}
              />
            </MenuItem>
            {!hideFeatures && <AchievementsMenuItem closeProfile={closeProfile} />}

            <Divider className={classes.divider} />

            <MenuItem id='appearance-menu-item' onClick={(e) => e.stopPropagation()}>
              <IconRowSpan text='Appearance' iconComponent={<AppearanceToggle />} />
            </MenuItem>

            <PrivacyModeToggle />

            {feedbackEnabled && showHiddenMenu && (
              <MenuItem
                onClick={() => {
                  trackTopBarNavMenuClick({ menuItem: 'Send Feedback', section: 'topbar-profile' });
                  closeProfile();
                  createDialog(
                    rootUiTypes.mkRootUiData({
                      id: 'report-error',
                      type: DIALOG_TYPE_REPORT_ERROR,
                      allowNesting: true,
                      props: ImmutableMap({
                        feedbackSubject: 'send feedback',
                        directedBySupport: true,
                      }),
                    }),
                  );
                }}
                id='send-feedback'
              >
                <Typography variant='paragraphRegular'>Send Feedback</Typography>
              </MenuItem>
            )}

            {showHiddenMenu && (
              <MenuItem
                onClick={() => {
                  trackTopBarNavMenuClick({ menuItem: 'Capture Current State', section: 'topbar-profile' });
                  closeProfile();
                  const fileName = `${dataset?.id || 'no-dataset'} [${DateTime.local().toISODate()}].json`;
                  saveStore(fileName, false);
                }}
                id='capture-state'
              >
                <Typography variant='paragraphRegular'>Capture Current State</Typography>
              </MenuItem>
            )}

            <Divider />

            <MenuItem onClick={logoutClick} id='my-account-sign-out'>
              <Typography variant='paragraphRegular'>Sign Out</Typography>
            </MenuItem>
          </MenuList>
        </div>
      </Popover>
    </>
  );
}

export default memo(TopBarProfileMenu);
